import * as Api from '@ViewModels';

interface ICanEditAutomationTemplateParams {
	template: Pick<Api.IAutomationTemplateReference, 'scope' | 'status'>;
	userRole?: Api.UserRole;
	impersonationContext?: Api.IImpersonationContext;
	/** When true, allows editing Industry-scoped (sample) templates from the platform admin panel */
	isAdminContext?: boolean;
}

export function canEditAutomationTemplate({
	template,
	userRole,
	impersonationContext,
	isAdminContext,
}: ICanEditAutomationTemplateParams) {
	const isAdmin = userRole === Api.EUserRole.SuperAdmin || userRole === Api.EUserRole.Admin;
	const impersonatedUserRole = impersonationContext?.user?.role;
	const isImpersonatingAccountAdmin =
		impersonatedUserRole === Api.EUserRole.SuperAdmin || impersonatedUserRole === Api.EUserRole.Admin;

	if (template.status === Api.TemplateStatus.Archived) {
		return false;
	}

	// Allow editing Industry (sample) templates from the platform admin panel
	if (template.scope === Api.TemplateScope.Industry) {
		return !!isAdminContext;
	}

	return isAdmin || isImpersonatingAccountAdmin || template.scope === Api.TemplateScope.User;
}

/**
 * Whether a platform admin working inside a customer account (impersonated routes) can edit a template.
 * Industry samples are excluded: they live in Levitate's account, so the account-scoped impersonated
 * writes (e.g. `impersonate/{accountId}/AutomationTemplate/{id}/Draft/commit`) 404 on them. Samples are
 * edited from the sample editor, or duplicated into the account first.
 */
export function canEditAutomationTemplateOnBehalfOfAccount({
	template,
	hasOnBehalfPermission,
}: {
	template: Pick<Api.IAutomationTemplateReference, 'scope' | 'status'>;
	hasOnBehalfPermission: boolean;
}) {
	return (
		hasOnBehalfPermission &&
		template.status !== Api.TemplateStatus.Archived &&
		template.scope !== Api.TemplateScope.Industry
	);
}
